import {Product, User} from '@hconnect/apiclient'
import {BackendFieldError} from '@hconnect/authenticator/src/ErrorMap'
import {trackEvent} from '@hconnect/common/logging/Analytics'
import {Button} from '@mui/material'
import {useMutation} from '@tanstack/react-query'
import {AxiosError} from 'axios'
import {useSnackbar} from 'notistack'
import {UseFormReturn} from 'react-hook-form'
import {useSelector} from 'react-redux'

import {api} from '../../../App.store'
import {UserInformationFormData} from '../../../components/UserInformation/UserInformationForm.types'
import {useTranslation} from '../../../hooks/useTranslation'
import {selectLoggedInUserProfile} from '../../../modules/LoggedInUser.selectors'
import {RoleAssignment} from '../../../modules/ManageUsers.selectors'
import {createUser} from '../../../modules/Users.actions'

export type InviteUserPayload = {
  user_id: string
  product: Product
}

type UserRequestData = {
  createUser: Partial<User>
  assignUserRoles: Omit<RoleAssignment, 'userId'>[]
  inviteUser?: Omit<InviteUserPayload, 'user_id'>[]
}

export const assignUserRole = async (payload: RoleAssignment) => {
  const response = await api.post<RoleAssignment>('/roles', payload)
  const {data: userRole} = response
  return userRole
}

const inviteUser = async (payload: InviteUserPayload) => {
  return await api.post('/users/invite', payload)
}

const deleteUser = async (userId: string) => {
  await api.delete(`/users/${userId}`)
}

const createUserWithRoles = async (data: UserRequestData) => {
  let newUserId: string | undefined

  try {
    const response = await createUser(data.createUser)
    newUserId = response.user_id

    for (const userRole of data.assignUserRoles) {
      await assignUserRole({...userRole, userId: newUserId})
    }

    if (data.inviteUser) {
      for (const invitationData of data.inviteUser) {
        await inviteUser({...invitationData, user_id: newUserId})
      }
    }

    return response
  } catch (e) {
    const error = e as AxiosError
    if (error.response?.status !== 403) {
      if (newUserId) {
        await deleteUser(newUserId)
      }
    }
    throw e
  }
}

export const useCreateUser = (
  formMethods: UseFormReturn<UserInformationFormData, object>,
  setShowInvitationDialog: (show: boolean) => void,
  setNewUser: (user: User) => void
) => {
  const {t} = useTranslation()
  const {enqueueSnackbar, closeSnackbar} = useSnackbar()
  const loggedInUserProfile = useSelector(selectLoggedInUserProfile)

  const mutation = useMutation(createUserWithRoles, {
    onSuccess: (data) => {
      trackEvent('adminUserCreateSuccess', {
        userId: loggedInUserProfile?.user_id,
        product: 'adminConsole',
        createdUserId: data.user_id
      })
      enqueueSnackbar(t('widgetUserCreation.form.successCreateMsg'), {
        anchorOrigin: {vertical: 'bottom', horizontal: 'center'},
        variant: 'success',
        action: (key) => (
          <Button
            style={{color: '#FFFFFF', borderColor: 'transparent'}}
            onClick={() => closeSnackbar(key)}
          >
            Close
          </Button>
        )
      })
      setShowInvitationDialog(true)
      setNewUser(data)
    },
    onError: (e) => {
      const error = e as AxiosError<{extendedData: {fieldErrors: BackendFieldError[]}}>
      trackEvent('adminConsoleError', {
        userId: loggedInUserProfile?.user_id,
        product: 'adminConsole',
        error: JSON.stringify(error)
      })
      if (error.response?.status === 403) {
        enqueueSnackbar(t('errorMessages.403_USER_ALREADY_EXISTS'), {
          anchorOrigin: {vertical: 'bottom', horizontal: 'center'},
          variant: 'error',
          action: (key) => (
            <Button
              style={{color: '#FFFFFF', borderColor: 'transparent'}}
              onClick={() => closeSnackbar(key)}
            >
              Close
            </Button>
          )
        })
      } else if (error.response?.status === 409) {
        enqueueSnackbar(t('errorMessages.409_LESS_RESTRICTIVE_ROLE'), {
          anchorOrigin: {vertical: 'bottom', horizontal: 'center'},
          variant: 'error',
          action: (key) => (
            <Button
              style={{color: '#FFFFFF', borderColor: 'transparent'}}
              onClick={() => closeSnackbar(key)}
            >
              Close
            </Button>
          )
        })
      } else {
        const fieldErrors = error?.response?.data?.extendedData?.fieldErrors
        if (fieldErrors && fieldErrors?.length > 0) {
          fieldErrors.forEach((fieldError) => {
            const input = fieldError.fieldName.toLowerCase() === 'email' ? 'eMail' : 'mobileNumber'
            formMethods.setError(input, {
              message:
                fieldError.validationErrorType.toLowerCase() === 'exists'
                  ? input === 'eMail'
                    ? 'USER_EMAIL_ALREADY_EXISTS'
                    : 'USER_MOBILE_NUMBER_ALREADY_EXISTS'
                  : input === 'eMail'
                    ? 'INVALID_EMAIL_FORMAT'
                    : 'INVALID_MOBILE_NUMBER'
            })
          })
        } else {
          enqueueSnackbar(t('errorboundary.error'), {
            anchorOrigin: {vertical: 'bottom', horizontal: 'center'},
            variant: 'error',
            action: (key) => (
              <Button
                style={{color: '#FFFFFF', borderColor: 'transparent'}}
                onClick={() => closeSnackbar(key)}
              >
                Close
              </Button>
            )
          })
        }
      }
    }
  })
  const handleCreateUser = (data: UserRequestData) => {
    mutation.mutate(data)
  }

  return {
    handleCreateUser,
    isLoading: mutation.isLoading,
    isUserCreated: mutation.isSuccess
  }
}
